import BaseHttpService from "JS/services/BaseHttpService";
import { IBaseHttpService } from "JS/types/Http";
import { IContactInfoUpdateRequest, IDateAndTimeStepDropoffRequest, IDateAndTimeStepTimeSlotRequest, IServiceInfoUpdateRequest, IWorkOrderSessionResource } from "../types/session";
import { ICustomerVehicle } from "JS/types/Automotive";

export interface IAutomotiveSchedulerStepService {
	getSession(): Promise<IWorkOrderSessionResource>;
	submitContactInfo(contactInfoRequest: IContactInfoUpdateRequest): Promise<IWorkOrderSessionResource>;
	updateServices(serviceBlainNumbers: string[]): Promise<IWorkOrderSessionResource>;
	submitServiceStep(serviceStepRequest: IServiceInfoUpdateRequest): Promise<IWorkOrderSessionResource>;
	requestTimeSlotAppointment(timeSlot: IDateAndTimeStepTimeSlotRequest): Promise<IWorkOrderSessionResource>;
	requestDropoffAppointment(date: IDateAndTimeStepDropoffRequest): Promise<IWorkOrderSessionResource>;
	submitPaymentStep(prePay: boolean): Promise<IWorkOrderSessionResource>;
	submit(): Promise<IWorkOrderSessionResource>;
	setVehicle(vehicle: ICustomerVehicle): Promise<IWorkOrderSessionResource>;
}

const BASE_URL = "/automotive-scheduler/step";
export class AutomotiveSchedulerStepService implements IAutomotiveSchedulerStepService {
	constructor(private _httpService: IBaseHttpService = new BaseHttpService()) {}

	getSession(): Promise<IWorkOrderSessionResource> {
		return this._httpService.get<IWorkOrderSessionResource>(`${BASE_URL}/`);
	}
	submitContactInfo(contactInfoRequest: IContactInfoUpdateRequest): Promise<IWorkOrderSessionResource> {
		return this._httpService.post<IWorkOrderSessionResource>(`${BASE_URL}/contact`, contactInfoRequest);
	}
	updateServices(serviceBlainNumbers: string[]): Promise<IWorkOrderSessionResource> {
		return this._httpService.post<IWorkOrderSessionResource>(`${BASE_URL}/service/update`, serviceBlainNumbers);
	}
	submitServiceStep(serviceStepRequest: IServiceInfoUpdateRequest): Promise<IWorkOrderSessionResource> {
		return this._httpService.post<IWorkOrderSessionResource>(`${BASE_URL}/service`, serviceStepRequest);
	}
	requestTimeSlotAppointment(timeSlot: IDateAndTimeStepTimeSlotRequest): Promise<IWorkOrderSessionResource> {
		// When we post a Time Slot Appointment, we need the Date to have an unspecified TimeZone.
		// By converting to a string here in US locale format, we will post a value like `8/2/2022, 11:00:00 AM`,
		// which will be bound to a variable with DateTimeKind.Unspecified by the .NET server.
		// If we didn't do this, and used a Date object (containing offset for the local client) .NET will
		// interpret the value, set the time offset according to "local server time".
		const startString = timeSlot.timeSlot.start.toLocaleString('en-US');
		const endString = timeSlot.timeSlot.end.toLocaleString('en-US');
		const stringSerializedTimeSlot = {
			start: startString,
			end: endString
		};
		return this._httpService.post<IWorkOrderSessionResource>(`${BASE_URL}/appointment/time-slot`, {
			timeSlot: stringSerializedTimeSlot
		});
	}
	requestDropoffAppointment(date: IDateAndTimeStepDropoffRequest): Promise<IWorkOrderSessionResource> {
		const dateString = date.date.toLocaleString('en-US');
		const payload = {
			date: dateString
		};
		return this._httpService.post<IWorkOrderSessionResource>(`${BASE_URL}/appointment/dropoff`, payload);
	}

	submitPaymentStep(prePay: boolean): Promise<IWorkOrderSessionResource> {
		return this._httpService.post<IWorkOrderSessionResource>(`${BASE_URL}/payment`, { prePay });
	}

	submit(): Promise<IWorkOrderSessionResource> {
		return this._httpService.post(BASE_URL, {});
	}

	setVehicle(vehicle: ICustomerVehicle): Promise<IWorkOrderSessionResource> {
		return this._httpService.post(`${BASE_URL}/set-vehicle`, vehicle);
	}
}